import {
  BookCheckIcon,
  DownloadIcon,
  PuzzleIcon,
  ShoppingCartIcon,
  SparklesIcon,
  StoreIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceLink,
  PreferenceScreen,
  PreferenceSwitch,
} from '../../components/Preference'
import {
  cogoCheckAuth,
  probeCogoExtensionFast,
  type CogoAuth,
  type CogoExtensionStatus,
} from '../../lib/cogoExtension'
import {
  cogoExtensionUiMode,
  getCogoExtensionPrefs,
  markCogoExtensionDownloaded,
  type CogoExtensionPrefs,
  type CogoExtensionUiMode,
} from '../../lib/cogoExtensionPrefs'
import {
  isCogoBasketEnabled,
  setCogoBasketEnabled,
  subscribeCogoBasketEnabled,
} from '../../lib/cogoBasketPrefs'
import {
  isCogoEnrichEnabled,
  setCogoEnrichEnabled,
  subscribeCogoEnrichEnabled,
} from '../../lib/cogoEnrichPrefs'
import { ME_KEY } from '../../lib/auth-client'
import { api } from '../../lib/password'
import { fetcher } from '../../lib/swr'
import { isShop, type Shop } from '../../lib/shop'
import { useCogoBasketStatus } from '../../lib/useCogoBasketStatus'
import { useCogoEnrichStatus } from '../../lib/useCogoEnrichStatus'

function formatSignedInAgo(at: number): string {
  const mins = Math.max(0, Math.round((Date.now() - at) / 60_000))
  if (mins < 1) return 'zojuist'
  if (mins < 60) return `${mins} min geleden`
  const hours = Math.round(mins / 60)
  if (hours < 48) return `${hours} u geleden`
  const days = Math.round(hours / 24)
  return `${days} d geleden`
}

function isAuthUncertain(auth: CogoAuth | null): boolean {
  if (auth == null) return true
  return auth.status === 'error' || auth.status === 'timeout'
}

function authSummary(
  auth: CogoAuth | null,
  prefs: CogoExtensionPrefs,
  authResolved: boolean
): string | null {
  if (!authResolved || isAuthUncertain(auth)) {
    return 'Sessie controleren…'
  }
  if (auth?.loggingIn || auth?.status === 'logging_in') {
    return 'Xtra-login bezig'
  }
  if (auth?.blocked) return 'Antibot-blokkade'
  if (auth?.loggedIn) return 'Ingelogd op Collect&Go'
  if (auth?.status === 'no_tab') {
    if (prefs.recentlySignedIn && prefs.lastSignedInAt != null) {
      return `Laatst ingelogd ${formatSignedInAgo(prefs.lastSignedInAt)}`
    }
    return 'Geen Collect&Go-tab open'
  }
  if (auth && !auth.loggedIn) return 'Niet ingelogd'
  if (prefs.recentlySignedIn && prefs.lastSignedInAt != null) {
    return `Laatst ingelogd ${formatSignedInAgo(prefs.lastSignedInAt)}`
  }
  if (prefs.everSignedIn && prefs.lastSignedInAt != null) {
    return `Laatst ingelogd ${formatSignedInAgo(prefs.lastSignedInAt)}`
  }
  return null
}

function statusCopy(
  ext: CogoExtensionStatus | null,
  auth: CogoAuth | null,
  prefs: CogoExtensionPrefs,
  authResolved: boolean
): { title: string; summary: string } {
  if (ext?.installed) {
    const version = ext.version ?? prefs.lastVersion ?? '?'
    const tabs = `${ext.cogoTabCount} Cogo-tab${
      ext.cogoTabCount === 1 ? '' : 's'
    } open`
    const authLine = authSummary(auth, prefs, authResolved)
    return {
      title: 'Extensie actief',
      summary: authLine ? `v${version} — ${authLine}` : `v${version} — ${tabs}`,
    }
  }
  if (ext == null) {
    if (prefs.everActivated || prefs.recentlyActive) {
      return {
        title: 'Extensie',
        summary: [
          prefs.lastVersion ? `v${prefs.lastVersion}` : null,
          'controleren…',
        ]
          .filter(Boolean)
          .join(' — '),
      }
    }
    if (prefs.downloadedAt != null) {
      return {
        title: 'Extensie laden',
        summary: 'Zip gedownload — laad unpacked in chrome://extensions',
      }
    }
    return {
      title: 'Extensie',
      summary: 'Nog niet geïnstalleerd',
    }
  }
  if (prefs.everActivated || prefs.recentlyActive) {
    return {
      title: 'Extensie niet bereikbaar',
      summary: 'Tik voor installatiehulp',
    }
  }
  if (prefs.downloadedAt != null) {
    return {
      title: 'Extensie nog niet actief',
      summary: 'Klik op "Load unpacked" in chrome://extensions',
    }
  }
  return {
    title: 'Extensie niet gevonden',
    summary: 'Download de zip en laad unpacked in chrome://extensions',
  }
}

/** Definitive signed-in / blocked / login-flow — show immediately. */
function isAuthDefinitive(auth: CogoAuth): boolean {
  if (isAuthUncertain(auth)) return false
  if (auth.loggedIn || auth.blocked) return true
  if (auth.loggingIn || auth.status === 'logging_in') return true
  if (auth.status === 'no_tab' || auth.status === 'needs_login') return true
  return false
}

export default function CogoSettings() {
  const [ext, setExt] = useState<CogoExtensionStatus | null>(null)
  const [auth, setAuth] = useState<CogoAuth | null>(null)
  const [authResolved, setAuthResolved] = useState(false)
  const [prefs, setPrefs] = useState(() => getCogoExtensionPrefs())
  // Lock layout for this visit so probe / first activation doesn't resize the block.
  const [mode] = useState<CogoExtensionUiMode>(() => cogoExtensionUiMode())
  const [showHelp, setShowHelp] = useState(false)
  const [enrichOn, setEnrichOn] = useState(() => isCogoEnrichEnabled())
  const [basketOn, setBasketOn] = useState(() => isCogoBasketEnabled())
  const me = useSWR<{
    tenant?: { defaultShop?: string } | null
  }>(ME_KEY, fetcher)
  const [defaultShop, setDefaultShop] = useState<Shop>('manual')
  const { label } = useCogoEnrichStatus()
  const { label: basketLabel } = useCogoBasketStatus()
  const notLoggedInStreak = useRef(0)

  useEffect(() => {
    const shop = me.data?.tenant?.defaultShop
    if (isShop(shop)) setDefaultShop(shop)
  }, [me.data?.tenant?.defaultShop])

  const extInstalled = Boolean(ext?.installed)

  useEffect(() => {
    let cancelled = false
    async function tick() {
      const status = await probeCogoExtensionFast()
      if (cancelled) return
      setExt(status)
      if (!status.installed) {
        setAuth(null)
        setAuthResolved(false)
        notLoggedInStreak.current = 0
        return
      }
      setPrefs(getCogoExtensionPrefs())
      if (status.loggingIn) {
        notLoggedInStreak.current = 0
        setAuth({
          loggedIn: false,
          blocked: false,
          loggingIn: true,
          status: 'logging_in',
        })
        setAuthResolved(true)
        return
      }
      if (status.cogoTabCount === 0) {
        notLoggedInStreak.current = 0
        setAuth({
          loggedIn: false,
          blocked: false,
          status: 'no_tab',
          message: 'Geen Collect&Go-tab open',
        })
        setAuthResolved(true)
        return
      }
      const next = await cogoCheckAuth()
      if (cancelled) return
      setPrefs(getCogoExtensionPrefs())

      // Content script not ready / timeout — keep loading, don't flash "Niet ingelogd".
      if (isAuthUncertain(next)) {
        return
      }

      if (next.loggedIn || isAuthDefinitive(next)) {
        notLoggedInStreak.current = 0
        setAuth(next)
        setAuthResolved(true)
        return
      }

      // Plain logged-out: confirm twice so a transient Aanmelden link doesn't flash.
      notLoggedInStreak.current += 1
      const prefsNow = getCogoExtensionPrefs()
      if (notLoggedInStreak.current >= 2 || !prefsNow.recentlySignedIn) {
        setAuth(next)
        setAuthResolved(true)
      }
    }

    // While the missing/install notice is up: every 10s + on focus.
    // Once active: keep a faster auth poll.
    const intervalMs = extInstalled ? 2000 : 10_000

    void tick()
    const id = setInterval(() => void tick(), intervalMs)

    function onFocus() {
      if (extInstalled) return
      if (document.visibilityState === 'hidden') return
      void tick()
    }
    if (!extInstalled) {
      window.addEventListener('focus', onFocus)
      document.addEventListener('visibilitychange', onFocus)
    }

    return () => {
      cancelled = true
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [extInstalled])

  useEffect(() => subscribeCogoEnrichEnabled(setEnrichOn), [])
  useEffect(() => subscribeCogoBasketEnabled(setBasketOn), [])

  const { title, summary } = statusCopy(ext, auth, prefs, authResolved)
  // setup / installing always show actions; ready only after explicit tap (no probe-driven expand).
  const helpOpen = mode !== 'ready' || showHelp
  const needsAttention = ext != null && !ext.installed

  function downloadExt() {
    markCogoExtensionDownloaded()
    setPrefs(getCogoExtensionPrefs())
    window.location.href = '/downloads/cogo-extension.zip'
  }

  function copyExtensionsUrl() {
    void navigator.clipboard.writeText('chrome://extensions')
    toast.success('URL gekopieerd')
  }

  return (
    <PreferenceScreen title="Collect&Go" backHref="/settings">
      <PreferenceCategory filled title="Voorkeuren">
        <PreferenceLink
          icon={<BookCheckIcon className="h-6 w-6" />}
          title="Producten"
          summary="Wat moet er besteld worden per ingrediënt?"
          href="/settings/cogo/products"
        />
        <PreferenceSwitch
          icon={<StoreIcon className="h-6 w-6" />}
          title="Collect&Go als standaardwinkel"
          summary={
            defaultShop === 'cogo'
              ? 'Nieuwe bestellingen openen in Collect&Go-modus'
              : 'Handmatige checklist blijft de standaard'
          }
          value={defaultShop === 'cogo'}
          onValueChange={(v) => {
            const prev = defaultShop
            const next: Shop = v ? 'cogo' : 'manual'
            setDefaultShop(next)
            void api
              .patch('/api/tenants', { defaultShop: next })
              .then(() => me.mutate())
              .catch((e: unknown) => {
                setDefaultShop(prev)
                toast.error(e instanceof Error ? e.message : 'Opslaan mislukt')
              })
          }}
        />
      </PreferenceCategory>

      <PreferenceCategory filled title="Chrome-extensie">
        {mode === 'ready' && !showHelp && needsAttention ? (
          <PreferenceButton
            icon={<PuzzleIcon className="h-6 w-6" />}
            title={title}
            summary={summary}
            onPress={() => setShowHelp(true)}
          />
        ) : (
          <PreferenceInfo
            icon={<PuzzleIcon className="h-6 w-6" />}
            title={title}
            summary={summary}
          />
        )}
        {helpOpen ? (
          <>
            {mode === 'setup' ? (
              <PreferenceInfo
                title="Hoe het werkt"
                summary="Sync vanuit een bestelling gebruikt je echte Chrome-sessie. Log zelf in bij Collect&Go in die tab als dat gevraagd wordt."
              />
            ) : null}
            <PreferenceButton
              icon={<DownloadIcon className="h-6 w-6" />}
              title={
                prefs.downloadedAt != null
                  ? 'Opnieuw downloaden'
                  : 'Extensie downloaden'
              }
              summary="Zip · chrome://extensions → Load unpacked"
              onPress={downloadExt}
            />
            <PreferenceButton
              title="Open chrome://extensions"
              summary="Kopieert chrome://extensions"
              onPress={copyExtensionsUrl}
            />
          </>
        ) : null}
        <PreferenceSwitch
          icon={<SparklesIcon className="h-6 w-6" />}
          title="Opzoekingen in achtergrond"
          summary={enrichOn ? label : 'Prijzen, hoeveelheden, alternatieven'}
          value={enrichOn}
          onValueChange={(v) => {
            setCogoEnrichEnabled(v)
            setEnrichOn(v)
          }}
        />
        <PreferenceSwitch
          icon={<ShoppingCartIcon className="h-6 w-6" />}
          title="Winkelwagen in achtergrond"
          summary={
            basketOn ? basketLabel : 'Producten klaarzetten om te bestellen'
          }
          value={basketOn}
          onValueChange={(v) => {
            setCogoBasketEnabled(v)
            setBasketOn(v)
          }}
        />
      </PreferenceCategory>
    </PreferenceScreen>
  )
}
